import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from './helmet';
import { enabledMarketingLocales, getLocaleFromPath, type MarketingLocale } from '../../config/localeRoutes';
import { filterHreflangAlternatesForActiveLocales } from '../../config/marketingHreflangClusters';
import { serializeJsonLd } from './serializeJsonLd';
import {
  marketingCanonicalUrl,
  normalizeMarketingPathForCanonical,
  PRO_MARKETING_BASE,
} from './marketingCanonicalUrl';

const OG_LOCALE_BY_LANGUAGE: Record<string, string> = {
  fr: 'fr_FR',
  en: 'en_GB',
  es: 'es_ES',
  de: 'de_DE',
  it: 'it_IT',
  nl: 'nl_NL',
};

/**
 * Chemins alternatifs pour hreflang.
 * Schéma tranché (SEO international Pro) : codes langue ISO 639-1
 * (`fr`, `en`, `es`, `de`, `it`) — pas de variantes région (es-ES / en-GB)
 * dans hreflang HTML/sitemap, pour rester aligné avec booking et éviter
 * des clusters hreflang incomplets. Le ciblage pays se fait via contenu + GSC.
 */
/** Chemins alternatifs : clés = codes hreflang (`fr`, `fr-BE`, `en`, …). */
export type AlternateLocalePaths = Record<string, string> & { fr?: string };

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalPath?: string;
  /** Chemins alternatifs pour hreflang (ex. { fr: '/', es: '/es' }). */
  alternatePaths?: AlternateLocalePaths;
  ogImage?: string;
  ogType?: string;
  keywords?: string;
  /** Ex. `index, follow` — affiche une balise meta robots explicite quand défini. */
  robots?: string;
  noindex?: boolean;
  schemaData?: Record<string, unknown>[];
  children?: React.ReactNode;
}

export default function SEOHead({
  title,
  description,
  canonicalPath,
  alternatePaths,
  ogImage = '/og-image.jpg',
  ogType = 'website',
  keywords,
  robots,
  noindex = false,
  schemaData = [],
  children,
}: SEOHeadProps) {
  const { pathname } = useLocation();
  const { t } = useTranslation('login');
  const pathLocale = getLocaleFromPath(pathname) ?? 'fr';
  const language = pathLocale;
  const ogLocale = OG_LOCALE_BY_LANGUAGE[language] ?? OG_LOCALE_BY_LANGUAGE.fr;
  // Fallback pathname → évite « Duplicate without user-selected canonical » si canonicalPath oublié.
  const resolvedPath = canonicalPath ?? (noindex ? undefined : pathname);
  const canonicalUrl =
    !noindex && resolvedPath != null ? marketingCanonicalUrl(pathname, resolvedPath) : undefined;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${PRO_MARKETING_BASE}${ogImage}`;

  const activeLocales = enabledMarketingLocales();
  const hreflangEntries = alternatePaths
    ? Object.entries(filterHreflangAlternatesForActiveLocales(alternatePaths, activeLocales))
    : [];

  const helmetKey = `${normalizeMarketingPathForCanonical(pathname)}|${language}`;

  return (
    <Helmet key={helmetKey} htmlAttributes={{ lang: language }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {hreflangEntries.map(([hreflangCode, altPath]) => (
        <link
          key={hreflangCode}
          rel="alternate"
          hrefLang={hreflangCode}
          href={marketingCanonicalUrl(pathname, altPath)}
        />
      ))}
      {alternatePaths?.fr &&
        (activeLocales.includes('fr') || alternatePaths['fr-BE'] || alternatePaths['nl-BE']) && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={marketingCanonicalUrl(pathname, alternatePaths.fr)}
        />
      )}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && robots && <meta name="robots" content={robots} />}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={t('title')} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />

      {/* Schema.org JSON-LD */}
      {schemaData.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {serializeJsonLd(schema)}
        </script>
      ))}

      {children}
    </Helmet>
  );
}
